import { useEffect, useRef, useState } from 'react';

type UseInfiniteScrollParams = {
  hasMore: boolean;
  /** may be called again before the previous call settles - the caller ignores calls while it loads */
  onLoadMore: () => void;
};

/**
 * Calls `onLoadMore` when the sentinel scrolls into view at the end of the root - the legacy
 * `infinite-scroll` directive's "bottom reached" rule. Attach `sentinelRef` to the sentinel and `rootRef` to
 * the element that scrolls the list (e.g. `ScrollBox`'s `scrollRef`); without a root nothing is observed.
 *
 * Both are callback refs into state, so a remounted root re-roots the observer. The root is attached, never
 * discovered from computed `overflow`: `ScrollBox` turns scrollable only after its ResizeObserver re-render,
 * so a lookup on mount picks an outer ancestor. Never the window either: the AngularJS shell repositions the
 * page scroll when page-level content grows.
 */
export const useInfiniteScroll = ({ hasMore, onLoadMore }: UseInfiniteScrollParams) => {
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null);
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    if (!sentinel || !root || !hasMore) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onLoadMoreRef.current();
        }
      },
      { root },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [sentinel, root, hasMore]);

  return { sentinelRef: setSentinel, rootRef: setRoot };
};
